/**
 * Admin session storage, and the trade-off "Remember me for 30 days" makes.
 *
 * ## Where the token lives, and why that is now a choice
 *
 * By default the session lives in `sessionStorage`: scoped to the tab, gone when the tab
 * closes, so a shared workstation does not leak an operator session into the next person's
 * tab. That is still exactly what happens when the box on the login form is left unticked,
 * and it is still the default.
 *
 * When the operator ticks it, the session is written to `localStorage` instead, together with
 * the rotating remember-me token the API issues and an explicit 30-day ceiling. This file used
 * to carry a flat "not `localStorage`" note; that note is gone rather than left standing,
 * because a comment describing behaviour the code no longer has is worse than no comment.
 *
 * ## What the thirty days actually are
 *
 * Not a thirty-day bearer token. The access token stays an hour whatever the box says, because
 * a thirty-day bearer token is one nobody can revoke and "sign out everywhere" has to mean
 * something on an operator console above all. What the box buys is a **rotating lineage**:
 * `rememberToken` is single-use, spending it at `POST /admin/auth/refresh` returns a fresh hour
 * and a fresh token, and presenting a spent one kills the whole lineage. So a stolen console
 * token is a bounded loss rather than a month of quiet access.
 *
 * ## The trade, stated plainly
 *
 *   - **What it costs.** A token in `localStorage` outlives the tab and is readable by any
 *     script on this origin. The console ships no third-party script and renders no
 *     user-generated HTML, so the realistic threat is physical: a shared machine where the next
 *     person opens the console and is already signed in.
 *   - **What answers that.** `clearSession` wipes **both** stores, so signing out leaves nothing
 *     behind in either; `AuthContext` burns the server-side lineage on the way out; and the
 *     ceiling below is a hard stop the client enforces on every load whatever the tokens say.
 *   - **Two ceilings, not one.** The server's `rememberTokenExpiresAt` and this file's own
 *     30-day stop are both honoured, whichever comes first. Trusting only the server's would
 *     mean a deployment that issued a longer lineage silently extended a promise this console
 *     made to an operator; trusting only ours would mean holding a token the server has already
 *     retired.
 */
import type { AdminMe, AdminSession } from '../contracts/types.js';

const TOKEN_KEY = 'genex.admin.token';
const EXPIRES_KEY = 'genex.admin.expiresAt';
const ME_KEY = 'genex.admin.me';
/** The rotating remember-me token, and the two stops on it. Only ever in the device store. */
const REMEMBER_TOKEN_KEY = 'genex.admin.rememberToken';
const REMEMBER_TOKEN_EXPIRES_KEY = 'genex.admin.rememberTokenExpiresAt';
const REMEMBER_UNTIL_KEY = 'genex.admin.rememberUntil';

/** The ceiling a ticked box buys, in days. */
export const REMEMBER_ME_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The slice of `Storage` this module uses.
 *
 * Declared rather than taking the DOM `Storage` type so the rules below are testable in node
 * without a DOM, which is where the console's other rule modules are tested.
 */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Both stores, so every function here can be handed fakes. */
export interface SessionStores {
  /** Tab-scoped. The default, and where an unticked box still writes. */
  readonly tab: KeyValueStore;
  /** Device-scoped. Where a ticked box writes, always with a ceiling beside it. */
  readonly device: KeyValueStore;
}

/** What a login (or a refresh) hands back, before it is filed anywhere. */
export interface SessionCredentials {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly me: AdminMe;
  /** Present only when remember-me was asked for and the API issued a lineage. */
  readonly rememberToken?: string | undefined;
  readonly rememberTokenExpiresAt?: string | undefined;
}

export interface StoredSession extends SessionCredentials {
  /** True when this session came out of the remembered store. */
  readonly remembered: boolean;
  /** This console's own 30-day stop. Null for a tab-scoped session. */
  readonly rememberUntil: string | null;
}

/**
 * The real browser stores, or null where there are none.
 *
 * Null rather than a throw: a build step, a test, or a server render has no storage, and a
 * console that cannot read a session should behave exactly like one that found none.
 */
export function browserStores(): SessionStores | null {
  try {
    if (typeof globalThis.localStorage === 'undefined') return null;
    if (typeof globalThis.sessionStorage === 'undefined') return null;
    return { tab: globalThis.sessionStorage, device: globalThis.localStorage };
  } catch {
    // Storage access can be refused outright (private mode, a blocked third-party context).
    return null;
  }
}

/** The ceiling for a session remembered right now. Pure, so the arithmetic is testable. */
export function rememberCeiling(now: number, days: number = REMEMBER_ME_DAYS): string {
  return new Date(now + days * DAY_MS).toISOString();
}

function parse(value: string | null): number | null {
  if (value === null) return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
}

/**
 * When a remembered session stops being renewable: the sooner of this console's 30-day stop
 * and the server's own expiry for the lineage.
 *
 * Null when neither can be read, which every caller treats as "not renewable". A remembered
 * session with no readable stop is not something to keep alive on a guess.
 */
export function rememberDeadline(
  rememberUntil: string | null,
  rememberTokenExpiresAt: string | null | undefined,
): number | null {
  const ours = parse(rememberUntil);
  const theirs = parse(rememberTokenExpiresAt ?? null);
  if (ours !== null && theirs !== null) return Math.min(ours, theirs);
  return ours ?? theirs;
}

/**
 * When the *access token* stops working, which is a different question.
 *
 * Bounded by the remember deadline as well, so a server that issued a token outliving the
 * lineage cannot outlive this console's promise either.
 */
export function effectiveExpiry(
  accessTokenExpiresAt: string,
  rememberUntil: string | null,
  rememberTokenExpiresAt?: string | null,
): number | null {
  const token = parse(accessTokenExpiresAt);
  if (token === null) {
    // The token is the credential; a stop with nothing to stop is not a session.
    return null;
  }
  const deadline = rememberDeadline(rememberUntil, rememberTokenExpiresAt);
  return deadline === null ? token : Math.min(token, deadline);
}

/** Whether the access token in a stored session is still usable at `now`. */
export function isLive(session: StoredSession, now: number): boolean {
  const ends = effectiveExpiry(
    session.accessTokenExpiresAt,
    session.rememberUntil,
    session.rememberTokenExpiresAt ?? null,
  );
  return ends !== null && ends > now;
}

/**
 * Whether this session can be renewed: it has a remember token and neither stop has passed.
 *
 * Independent of `isLive`, deliberately. A session whose access token expired overnight is not
 * live and *is* renewable, and those are the two questions the boot path has to be able to ask
 * separately — collapsing them is how an operator with a valid 30-day lineage gets bounced to
 * the login screen at the start of every day.
 */
export function isRenewable(session: StoredSession, now: number): boolean {
  if (session.rememberToken === undefined || session.rememberToken === '') return false;
  const deadline = rememberDeadline(session.rememberUntil, session.rememberTokenExpiresAt ?? null);
  return deadline !== null && deadline > now;
}

function readFrom(store: KeyValueStore, remembered: boolean): StoredSession | null {
  const accessToken = store.getItem(TOKEN_KEY);
  const accessTokenExpiresAt = store.getItem(EXPIRES_KEY);
  const rawMe = store.getItem(ME_KEY);
  if (accessToken === null || accessTokenExpiresAt === null || rawMe === null) return null;
  let me: AdminMe;
  try {
    me = JSON.parse(rawMe) as AdminMe;
  } catch {
    return null;
  }
  const rememberToken = remembered ? store.getItem(REMEMBER_TOKEN_KEY) : null;
  const rememberTokenExpiresAt = remembered ? store.getItem(REMEMBER_TOKEN_EXPIRES_KEY) : null;
  return {
    accessToken,
    accessTokenExpiresAt,
    me,
    remembered,
    rememberUntil: remembered ? store.getItem(REMEMBER_UNTIL_KEY) : null,
    ...(rememberToken === null ? {} : { rememberToken }),
    ...(rememberTokenExpiresAt === null ? {} : { rememberTokenExpiresAt }),
  };
}

function wipe(store: KeyValueStore): void {
  for (const key of [
    TOKEN_KEY,
    EXPIRES_KEY,
    ME_KEY,
    REMEMBER_TOKEN_KEY,
    REMEMBER_TOKEN_EXPIRES_KEY,
    REMEMBER_UNTIL_KEY,
  ]) {
    store.removeItem(key);
  }
}

/**
 * The session on this machine, from whichever store holds it, live or merely renewable.
 *
 * The tab store is read first. Both being populated at once is not the normal case —
 * `saveSession` clears the other one — but it happens when a second tab signs in as somebody
 * else while a remembered session sits on the device, and in that case the tab the operator is
 * actually looking at should win.
 *
 * A session that is neither live nor renewable is not merely ignored, it is **cleared**, so a
 * dead token cannot sit in a store waiting to be read by the next load.
 */
export function loadSession(
  stores: SessionStores | null = browserStores(),
  now: number = Date.now(),
): StoredSession | null {
  if (stores === null) return null;
  try {
    const candidates: readonly (readonly [KeyValueStore, boolean])[] = [
      [stores.tab, false],
      [stores.device, true],
    ];
    for (const [store, remembered] of candidates) {
      const session = readFrom(store, remembered);
      if (session === null) continue;
      if (!isLive(session, now) && !isRenewable(session, now)) {
        wipe(store);
        continue;
      }
      return session;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Write the session, into exactly one store.
 *
 * The other store is wiped first rather than left alone. Leaving it would mean a second session
 * sitting behind the live one, surfacing the moment this one expires — both confusing and, on a
 * shared machine, the exact leak the tab default exists to prevent.
 *
 * `now` seeds the 30-day ceiling. On a **refresh** the caller passes the ceiling it already
 * had, through `keepCeiling`: rotating the token must not push the stop out, or thirty days of
 * activity becomes an indefinite session one refresh at a time.
 */
export function saveSession(
  credentials: SessionCredentials,
  remember: boolean,
  stores: SessionStores | null = browserStores(),
  now: number = Date.now(),
  keepCeiling: string | null = null,
): void {
  if (stores === null) return;
  try {
    const target = remember ? stores.device : stores.tab;
    const other = remember ? stores.tab : stores.device;
    wipe(other);
    target.setItem(TOKEN_KEY, credentials.accessToken);
    target.setItem(EXPIRES_KEY, credentials.accessTokenExpiresAt);
    target.setItem(ME_KEY, JSON.stringify(credentials.me));
    if (remember) {
      target.setItem(REMEMBER_UNTIL_KEY, keepCeiling ?? rememberCeiling(now));
      if (credentials.rememberToken !== undefined && credentials.rememberToken !== '') {
        target.setItem(REMEMBER_TOKEN_KEY, credentials.rememberToken);
      } else {
        // No lineage came back, so there is nothing to renew with. The stored session stays
        // device-scoped and simply ends when its access token does, which is honest; keeping a
        // stale token here would mean presenting a spent one and killing a lineage on purpose.
        target.removeItem(REMEMBER_TOKEN_KEY);
      }
      if (credentials.rememberTokenExpiresAt !== undefined) {
        target.setItem(REMEMBER_TOKEN_EXPIRES_KEY, credentials.rememberTokenExpiresAt);
      } else {
        target.removeItem(REMEMBER_TOKEN_EXPIRES_KEY);
      }
    } else {
      target.removeItem(REMEMBER_UNTIL_KEY);
      target.removeItem(REMEMBER_TOKEN_KEY);
      target.removeItem(REMEMBER_TOKEN_EXPIRES_KEY);
    }
  } catch {
    // A storage write can be refused (quota, private mode). The session still works for this
    // page's lifetime because the token is held in memory; it simply will not survive a
    // reload, which is a better outcome than a sign-in that throws.
  }
}

/**
 * What a rotation should file: the session's *new* tokens and its *existing* identity.
 *
 * `POST /admin/auth/refresh` answers with an `AdminSession` — two tokens and their expiries —
 * and deliberately carries no `admin`. That is the contract and it is the right one: the
 * console has known who is signed in since the login response, and re-sending an operator's
 * identity on every hourly rotation is not a field to add to a strict response schema.
 *
 * It is a function rather than an inline spread in the caller because getting it wrong is
 * silent and expensive, and it was wrong. Reading `me` off the refresh response yields
 * `undefined`; `saveSession` then stores `JSON.stringify(undefined)`, which is not a string —
 * `Storage.setItem` coerces it to the literal `"undefined"` — and `readFrom` cannot parse that,
 * so the next load finds no session at all and the operator is back at the sign-in screen. The
 * thirty days ended one hour in, at the first rotation, and left no trace: no error, no 401,
 * just a login form the next morning.
 *
 * The ceiling is the caller's to carry — see `saveSession`'s `keepCeiling`. This function only
 * settles what a rotation may replace, which is the tokens and nothing else.
 */
export function rotatedCredentials(
  previous: Pick<StoredSession, 'me'>,
  next: AdminSession,
): SessionCredentials {
  return {
    accessToken: next.accessToken,
    accessTokenExpiresAt: next.accessTokenExpiresAt,
    me: previous.me,
    rememberToken: next.rememberToken,
    rememberTokenExpiresAt: next.rememberTokenExpiresAt,
  };
}

/**
 * Sign out, everywhere.
 *
 * Both stores, unconditionally, whichever one the session came from. A sign-out on a shared
 * workstation that only cleared the store it happened to read is a sign-out that leaves an
 * operator session behind for the next person, which is precisely what the tab default was
 * protecting against in the first place.
 */
export function clearSession(stores: SessionStores | null = browserStores()): void {
  if (stores === null) return;
  try {
    wipe(stores.tab);
    wipe(stores.device);
  } catch {
    /* nothing to do: there is no store to clear */
  }
}
